import {
  Avatar,
  Dialog,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { Hall } from '@weblite/ts-action/hall'
import { Room } from '@weblite/ts-action/room'
import { PrimaryButton } from '$common/buttons'
import { CloseIcon } from '$common/icons/close'
import { ChannelIcon, GroupIcon } from '$common/icons'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { joinHallViaLink } from '$logic/subscription'
import { openRoom } from '$logic/opened-room'
import hostConf from '$config/frontend'
import { HallAvatar } from '$common/avatar/hall-avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { useClientUserPermissions } from '$logic/subscription/react-hook'

const hidePrivateHallMemberCount = hostConf.VITE_HIDE_PRIVATE_HALL_MEMBER_COUNT

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 310px;
    height: 310px;
    min-width: 280px;
    min-height: 280px;

    border-radius: 16px;
    box-sizing: border-box;
    padding: 15px;
  }
`

const Root = styled(Stack)`
  width: 100%;
  height: 100%;

  //justify-content: space-between;
  align-items: center;
  overflow: hidden;
`

const TitleTypography = styled(Typography)`
  margin-right: 4px;
`

interface Props {
  hall: Hall
  room: Room
  link: string
  isOpen: boolean
}

export function JoinHallDialog({ hall, room, link, isOpen }: Props) {
  const TitleIconComponent = room.type === 'GROUP' ? GroupIcon : ChannelIcon
  const clientUserPermissions = useClientUserPermissions(room.id)

  const onJoin = async () => {
    analite('CHAT_JOIN', { source: 'INVITE_LINK', name: hall?.identifier })
    const { success } = await joinHallViaLink(
      link,
      undefined,
      closeCurrentModal,
    )
    if (success) await openRoom(room.id)
  }

  return (
    <StyledDialog open={isOpen} onClose={closeCurrentModal}>
      <Root>
        <Stack justifyContent="flex-start" sx={{ width: '100%' }}>
          <IconButton sx={{ width: 'fit-content' }} onClick={closeCurrentModal}>
            <CloseIcon sx={{ color: 'gray' }} />
          </IconButton>
        </Stack>

        <HallAvatar roomId={room.id} size={AvatarSize._3XL} />

        <Stack alignItems="center" sx={{ marginBottom: '30px' }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ maxWidth: '280px', marginTop: '15px', marginBottom: '10px' }}
          >
            <TitleIconComponent
              sx={{
                color: 'primary.main',
                width: '20px',
                height: '20px',
              }}
            />

            <TitleTypography variant="subtitle3" noWrap>
              {hall.name}
            </TitleTypography>
          </Stack>

          <Typography color="gray" variant="caption1">
            {room.type === 'CHANNEL'
              ? clientUserPermissions?.isOwner &&
                (!hidePrivateHallMemberCount || hall.public)
                ? `${toLocale(hall.membersCount)} ${t('common.subscriber')}`
                : ''
              : !hidePrivateHallMemberCount || hall.public
                ? `${toLocale(hall.membersCount)} ${t('common.member')}`
                : ''}
          </Typography>
        </Stack>

        <PrimaryButton
          fullWidth
          text={t(
            `joinHallDialog.${room.type === 'GROUP' ? 'joinGroup' : 'joinChannel'
            }`,
          )}
          onClick={onJoin}
        />
      </Root>
    </StyledDialog>
  )
}
